import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Download, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";

const COUNT_LABELS = {
  customer_orders: "Customer orders",
  invoices: "Invoices",
  products: "Product records",
  purchases: "Purchase records",
  payments: "Payments",
  credit_ledger: "Customer credit records",
  refunds: "Refunds",
  receipts: "Receiving records",
  receiving_sessions: "Receiving sessions",
  allocations: "Stock allocations",
  buffer_ledger: "Buffer ledger entries",
  ready_stock_ledger: "Ready stock entries",
  packing_jobs: "Packing jobs",
  shipments: "Shipments",
};

export default function ArchiveDeleteDrawer({ batchId, batchName, open, onOpenChange, onDeleted }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [typedDelete, setTypedDelete] = useState("");
  const [typedName, setTypedName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReview(null);
      setDownloaded(false);
      setConfirmed(false);
      setTypedDelete("");
      setTypedName("");
      return;
    }
    setLoading(true);
    api
      .post(`/batches/${batchId}/delete-review`)
      .then((r) => setReview(r.data))
      .catch((e) => {
        toast.error(apiError(e));
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, batchId, onOpenChange]);

  const downloadBackup = async () => {
    try {
      const res = await api.get(`/batches/${batchId}/delete-export`, {
        params: { token: review.token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = review.export_filename;
      a.click();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      toast.success("Backup downloaded — keep this file safe");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const canDelete =
    review?.export_verified && downloaded && confirmed && typedDelete === "DELETE" && typedName === batchName && !busy;

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/batches/${batchId}/permanent`, {
        data: { token: review.token, confirm_text: typedDelete, confirm_name: typedName, backup_downloaded: true },
      });
      toast.success(`Archive "${batchName}" permanently deleted`);
      onOpenChange(false);
      onDeleted();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="font-heading text-destructive">Permanently Delete Archive</DrawerTitle>
        </DrawerHeader>
        <div className="max-h-[75vh] space-y-3 overflow-y-auto px-4 pb-8" data-testid="archive-delete-drawer">
          {loading || !review ? (
            <div className="grid place-items-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-destructive border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="rounded-md border border-destructive/40 bg-red-50 p-3" data-testid="archive-delete-review">
                <p className="font-heading text-base font-extrabold">{review.batch.name}</p>
                <p className="text-xs text-stone-600">
                  Closed {review.batch.closed_at ? format(new Date(review.batch.closed_at), "d MMM yyyy") : "—"}
                  {review.batch.closed_by_name ? ` by ${review.batch.closed_by_name}` : ""}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]">
                  {Object.entries(review.counts).map(([k, v]) => (
                    <div key={k} className="flex justify-between rounded-md border bg-white px-2 py-1">
                      <span className="truncate text-stone-600">{COUNT_LABELS[k] || k}</span>
                      <span className="font-bold tabular-nums">{v}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-[11px] font-bold text-destructive">
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {review.warning}
                </p>
              </div>

              {!review.export_verified ? (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="archive-delete-backup-failed">
                  Deletion is blocked: a complete backup could not be verified ({review.export_error || "unknown reason"}).
                </p>
              ) : (
                <div className="space-y-2 rounded-md border bg-card p-3">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500">Step 1 — Verified backup</p>
                  <Button variant="outline" data-testid="archive-delete-download-backup" className="h-11 w-full" onClick={downloadBackup}>
                    <Download className="h-4 w-4" /> {downloaded ? "Download again" : "Download backup file"}
                  </Button>
                  <label className="flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      data-testid="archive-delete-backup-confirm"
                      className="mt-0.5 h-4 w-4"
                      checked={confirmed}
                      disabled={!downloaded}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />
                    <span>I have downloaded the backup file and stored it safely.</span>
                  </label>
                </div>
              )}

              <div className="space-y-2 rounded-md border bg-card p-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500">Step 2 — Type to confirm</p>
                <div className="space-y-1.5">
                  <Label className="text-xs">Type DELETE</Label>
                  <Input
                    data-testid="archive-delete-type-delete"
                    className="h-11"
                    placeholder="DELETE"
                    value={typedDelete}
                    onChange={(e) => setTypedDelete(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Type the exact batch name</Label>
                  <Input
                    data-testid="archive-delete-type-name"
                    className="h-11"
                    placeholder={batchName}
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                  />
                </div>
              </div>

              <Button
                variant="destructive"
                data-testid="archive-delete-final-button"
                className="h-12 w-full text-base"
                disabled={!canDelete}
                onClick={doDelete}
              >
                {busy ? "Deleting…" : "Permanently delete this archive"}
              </Button>
              <Button variant="outline" data-testid="archive-delete-cancel" className="h-11 w-full" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
